import { Component, ReactElement, ReactNode } from 'react';
import type { UploadProps } from 'antd';
import { Button, Upload as AntdUpload, UploadFile } from 'antd';

import { obsSignUrl } from '../../api/fileManagement';
import { bucketName } from '../../httpService/API';
import { getOSSUploadSignUrl, getFileMD5, OSSHeaders } from '@/api/grt/upload';

interface genUrlResult {
    success: boolean;
    data: string | undefined | null;
}
export enum UploadType {
    // eslint-disable-next-line no-unused-vars
    UNSET,
    // eslint-disable-next-line no-unused-vars
    VIDEO = 2,
    // eslint-disable-next-line no-unused-vars
    AUDIO = 3
}
// eslint-disable-next-line no-unused-vars
export enum Accept {
    // eslint-disable-next-line no-unused-vars
    UNSET = '*',
    // eslint-disable-next-line no-unused-vars
    VIDEO = 'video/mpeg,video/mp4,video/3gpp',
    // eslint-disable-next-line no-unused-vars
    AUDIO = 'audio/mpeg,audio/3gpp'
}
export interface Props {
    uploadItem?: ReactNode;
    uploadType?: UploadType;
    dirname?: string;
    className?: string;
    // eslint-disable-next-line no-unused-vars
    onChange?: (file: UploadFile, type?: any) => void | boolean | undefined;
    // eslint-disable-next-line no-unused-vars
    beforeUpload?: (file: UploadFile, name: string, md5: string, type?: any) => Promise<boolean | undefined | void>;
    // eslint-disable-next-line no-unused-vars
    processFunc?: (processNumber: number, file: UploadFile, xhr: XMLHttpRequest, obsResult: any) => void;
    // eslint-disable-next-line no-unused-vars
    completeFunc?: (obsResult: any, file: UploadFile) => void;
    // eslint-disable-next-line no-unused-vars
    errorFunc?: (error: any, file: UploadFile, obsResult: any) => void;
    // eslint-disable-next-line no-unused-vars
    itemRender?: (originNode: ReactElement, file: UploadFile, fileList: object[], actions: any) => ReactNode;

    maxSize?: number;
    multiple?: boolean;
    maxSizeAudio?: number;
}

type FileExtType = {
    // @ts-ignore
    [name: symbol]: string[];
};
type FileExtMap = {
    [name: number]: symbol;
};

export interface State {}

const FILE_EXT_SYMBOL_VIDEO = Symbol('VIDEO'),
    FILE_EXT_SYMBOL_AUDIO = Symbol('AUDIO'),
    FILE_EXT_SYMBOL_UNSET = Symbol('UNSET');
export const FILE_EXT_MAP: FileExtMap = {
    [UploadType.VIDEO]: FILE_EXT_SYMBOL_VIDEO,
    [UploadType.AUDIO]: FILE_EXT_SYMBOL_AUDIO,
    [UploadType.UNSET]: FILE_EXT_SYMBOL_UNSET
};
export const FILE_EXT: FileExtType = {
    [FILE_EXT_SYMBOL_VIDEO]: ['mp4', 'wmv', 'm4v', 'flv', 'rmvb', 'dat', 'mov', 'mkv', 'webm', 'avi', 'mpeg', '3gp', 'ogg'],
    [FILE_EXT_SYMBOL_AUDIO]: ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'amr', 'flac', 'aiff'],
    [FILE_EXT_SYMBOL_UNSET]: [
        'mp4',
        'wmv',
        'm4v',
        'flv',
        'rmvb',
        'dat',
        'mov',
        'mkv',
        'webm',
        'avi',
        'mpeg',
        '3gp',
        'ogg',
        'mp3',
        'wav',
        'm4a',
        'aac',
        'ogg',
        'amr',
        'flac',
        'aiff'
    ]
};
class ServiceUpload extends Component<Props, State> {
    state: State = {};

    // @ts-ignore
    xhrUploadFile(uploadUrl, file, headers: any = {}, obsResult: any = {}) {
        const xhr = new XMLHttpRequest();
        xhr.open('put', uploadUrl);
        for (let key in headers) {
            const value = headers[key];
            if (key) {
                xhr.setRequestHeader(key, value);
            }
        }
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                console.debug('process:', event.loaded / event.total);
                if (this.props.processFunc) this.props.processFunc(event.loaded / event.total, file, xhr, obsResult);
            }
        };
        xhr.onerror = (err) => {
            if (this.props.errorFunc) {
                console.error(err);
                this.props.errorFunc(err, file, obsResult);
            }
        };
        xhr.onload = () => {
            if (xhr.status === 200) {
                if (this.props.completeFunc) {
                    console.debug('complete:');
                    const url: string = xhr.responseURL;
                    obsResult.fileUrl = url.substring(0, url.indexOf('?'));
                    this.props.completeFunc(obsResult, file);
                }
            }
        };
        xhr.send(file);
        return xhr;
    }

    get uploadItem(): ReactNode {
        return this.props.uploadItem ? this.props.uploadItem : <Button>上传</Button>;
    }
    // @ts-ignore
    getAccept(type): Accept {
        let accept: Accept = Accept.UNSET;
        switch (type) {
            case UploadType.AUDIO:
                accept = Accept.AUDIO;
                break;
            case UploadType.VIDEO:
                accept = Accept.VIDEO;
                break;
            case UploadType.UNSET:
                accept = Accept.UNSET;
                break;
        }
        return accept;
    }

    uploadOption: UploadProps = {
        accept: this.getAccept(this.props.uploadType),
        itemRender: this.props.itemRender || (() => null),
        beforeUpload() {
            return false;
        },
        multiple: this.props.multiple || false,
        onChange: async ({ file }) => {
            if (file.status) return false;
            const { name } = file;
            let { fileExt, fileName } = this.getFileExtAndName(name);
            const uploadType = this.props.uploadType || UploadType.UNSET,
                fileType: UploadType = this.getFileTypeByExt(fileExt);
            if (uploadType !== undefined && uploadType !== null) {
                const fileExtMap = FILE_EXT_MAP[uploadType];
                if (!fileExtMap) {
                    if (this.props.errorFunc) {
                        this.props.errorFunc(
                            {
                                message: '文件格式不支持',
                                size: file.size,
                                maxSize: this.props.maxSize
                            },
                            file,
                            {}
                        );
                    }
                    return false;
                }
                // @ts-ignore
                const extList = FILE_EXT[fileExtMap];
                if (fileExt && !extList.includes(fileExt.toLowerCase())) {
                    if (this.props.errorFunc) {
                        this.props.errorFunc(
                            {
                                message: '文件格式不支持',
                                size: file.size,
                                maxSize: this.props.maxSize
                            },
                            file,
                            {}
                        );
                    }
                    return false;
                }
            }
            if (this.props.onChange) {
                const changeFlag = this.props.onChange(file, fileType);
                if (!changeFlag && changeFlag !== undefined) return false;
            }
            //判断音频和视频的文件最大上传大小
            let maxSizeNum = this.props.maxSize;
            if (fileType === UploadType.VIDEO) {
                maxSizeNum = this.props.maxSize;
            } else if (fileType === UploadType.AUDIO) {
                maxSizeNum = this.props.maxSizeAudio;
            }
            // @ts-ignore
            if (this.props.maxSize && file.size && file.size > maxSizeNum) {
                if (this.props.errorFunc) {
                    this.props.errorFunc(
                        {
                            message: '文件大小超出限制',
                            size: file.size,
                            maxSize: this.props.maxSize
                        },
                        file,
                        {}
                    );
                }
                return false;
            }
            await getFileMD5(file).then(async (md5) => {
                const rename = md5 + '.' + fileExt,
                    fileKey =
                        (fileType === UploadType.VIDEO || fileType === UploadType.AUDIO) && this.props.dirname
                            ? 'input/user/AudioVideoTrans/' +
                              this.props.dirname +
                              '/' +
                              (fileType === UploadType.VIDEO ? 'video/' : 'audio/') +
                              rename
                            : this.props.dirname
                              ? this.props.dirname + '/' + rename
                              : rename,
                    bucket = fileType === UploadType.VIDEO ? bucketName.video : fileType === UploadType.AUDIO ? bucketName.audio : bucketName.other,
                    { data, success } = await getOSSUploadSignUrl(obsSignUrl, bucket, fileKey);
                if (!success) return false;

                if (this.props.beforeUpload) {
                    const beforeFlag = await this.props.beforeUpload(file, fileKey, md5 as string, fileType);
                    if (!beforeFlag && beforeFlag !== undefined) return false;
                }

                this.xhrUploadFile(data, file, OSSHeaders, {
                    bucketName: bucket,
                    objectKey: fileKey,
                    md5,
                    ext: fileExt,
                    fileName,
                    fileType
                });
            });
        }
    };

    getFileTypeByExt(ext: string) {
        ext = ext.toLowerCase();
        const extList = FILE_EXT;
        if (extList[FILE_EXT_SYMBOL_VIDEO].includes(ext)) return UploadType.VIDEO;
        else if (extList[FILE_EXT_SYMBOL_AUDIO].includes(ext)) return UploadType.AUDIO;
        return UploadType.UNSET;
    }

    getFileExtAndName(fileName: string) {
        const index = fileName.lastIndexOf('.'),
            ext = fileName.substring(index + 1),
            name = fileName.substring(0, index);
        return { fileExt: ext, fileName: name };
    }

    render(): ReactNode {
        return <AntdUpload {...this.uploadOption}>{this.uploadItem}</AntdUpload>;
    }
}

export default ServiceUpload;
